import { useState } from "react";
import { Link } from "react-router-dom";

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

const faqs = [
  {
    question: "Do you offer same-day CPR certification in Kissimmee?",
    answer: "Yes! Same-day certification is one of our most popular features at Cardiocraft CPR. You can book a class in the morning and walk out certified the same day. This is available for BLS Certification, BLS Renewal, CPR/AED, and First Aid courses.",
    category: "Certification",
  },
  {
    question: "Are your instructors Registered Nurses?",
    answer: "Absolutely. Every Cardiocraft CPR instructor is a credentialed Registered Nurse (RN) with real-world clinical experience. We believe CPR training is most impactful when it comes from healthcare professionals who have used these skills in actual emergencies.",
    category: "Instructors",
  },
  {
    question: "Is Cardiocraft CPR aligned with the American Heart Association (AHA)?",
    answer: "Yes. All of our courses follow American Heart Association guidelines and protocols. Our BLS, ACLS, and PALS certifications are AHA-aligned, ensuring your credentials are recognized by healthcare employers and institutions nationwide.",
    category: "Certification",
  },
  {
    question: "Do you offer group or corporate training sessions?",
    answer: "Yes! We offer fully customized group and corporate training for teams of any size. Whether you need on-site training at your business, hospital, school, or organization, we can bring our RN instructors to your location. Contact us for a custom quote.",
    category: "Group Training",
  },
  {
    question: "How much do your CPR courses cost?",
    answer: "BLS Certification and BLS Renewal are each $85. ACLS, PALS, CPR/AED, First Aid, and the BLS Instructor Course are priced based on class format and group size — please contact us for current pricing. Group training rates are quoted custom.",
    category: "Pricing",
  },
  {
    question: "Where is Cardiocraft CPR located?",
    answer: "We are located at 1627 East Vine Street, Suite 203, Kissimmee, FL 34744. Conveniently situated in Central Florida, we serve Kissimmee and the surrounding communities including Orlando, St. Cloud, and Osceola County.",
    category: "Location",
  },
  {
    question: "How long does a CPR certification class take?",
    answer: "Class length varies by course. BLS Certification takes approximately 3–4 hours. BLS Renewal is typically 2–3 hours. ACLS and PALS are full 1–2 day courses. CPR/AED and First Aid courses usually take 2–3 hours. All classes include hands-on skills testing.",
    category: "Course Details",
  },
  {
    question: "Do I need any prior experience to take a CPR class?",
    answer: "No prior experience is required for CPR/AED and First Aid courses — they are designed for the general public. BLS is ideal for healthcare students and professionals. ACLS and PALS require current BLS certification as a prerequisite.",
    category: "Course Details",
  },
  {
    question: "How do I book a class?",
    answer: "Booking is simple and takes just a few minutes. You can use our online booking form on this website, or email us at cardiocraftcpr@gmail.com. We'll confirm your class details and send you everything you need to prepare.",
    category: "Booking",
  },
  {
    question: "What is your cancellation and rescheduling policy?",
    answer: "We understand schedules change. Please contact us as soon as possible if you need to cancel or reschedule. We do our best to accommodate all requests and will work with you to find a new class time that fits your schedule.",
    category: "Booking",
  },
];

const categories = ["All", "Certification", "Instructors", "Pricing", "Group Training", "Course Details", "Booking", "Location"];

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [category, setCategory] = useState("All");

  const filtered = category === "All" ? faqs : faqs.filter((f) => f.category === category);

  return (
    <main className="pt-20">
      <title>CPR Certification FAQ Kissimmee | BLS ACLS PALS Questions | Cardiocraft CPR</title>
      <meta name="description" content="Answers to your top questions about CPR certification in Kissimmee, FL. Learn about BLS, ACLS, PALS costs, class duration, RN instructors, same-day certification, and booking at Cardiocraft CPR." />
      <meta name="keywords" content="CPR certification FAQ Kissimmee, BLS class questions, ACLS FAQ Florida, same-day CPR cert Kissimmee, CPR cost Kissimmee" />
      <link rel="canonical" href={`${SITE_URL}/faq`} />
      {/* Hero */}
      <section className="py-20 bg-brand-purple">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block border border-brand-gold/50 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
            Got Questions?
          </span>
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-white/80 text-lg">
            Everything you need to know about CPR certification in Kissimmee —
            same-day classes, RN instructors, AHA alignment, and more.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Filter Tags */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-4 py-2 text-sm font-semibold rounded-full border-2 transition-all cursor-pointer whitespace-nowrap ${
                  category === cat
                    ? "bg-brand-purple border-brand-purple text-white"
                    : "bg-white border-gray-200 text-gray-600 hover:border-brand-purple/50"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Accordion */}
          <div className="space-y-4">
            {filtered.map((faq, idx) => (
              <div
                key={faq.question}
                className={`border-2 rounded-xl overflow-hidden transition-all duration-200 ${
                  openIndex === idx ? "border-brand-purple" : "border-gray-100"
                }`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                  className="w-full flex items-start justify-between gap-4 p-6 text-left cursor-pointer"
                >
                  <div>
                    <span className="inline-block bg-brand-purple-pale text-brand-purple text-xs font-semibold px-2 py-0.5 rounded-full mb-2">
                      {faq.category}
                    </span>
                    <h3 className="font-semibold text-brand-black text-base leading-snug">
                      {faq.question}
                    </h3>
                  </div>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-200 ${
                    openIndex === idx ? "bg-brand-purple text-white" : "bg-gray-100 text-gray-500"
                  }`}>
                    <i className={`text-sm transition-transform duration-200 ${
                      openIndex === idx ? "ri-subtract-line" : "ri-add-line"
                    }`} />
                  </div>
                </button>
                {openIndex === idx && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Still Have Questions */}
          <div className="mt-14 bg-brand-purple-pale rounded-2xl p-8 text-center border border-brand-purple/10">
            <h3 className="font-serif text-2xl font-bold text-brand-black mb-3">
              Still Have Questions?
            </h3>
            <p className="text-gray-500 mb-6">
              Our team is happy to help. Reach out and we&apos;ll get back to you quickly.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="bg-brand-purple text-white font-semibold px-6 py-3 rounded-md hover:bg-brand-purple-dark transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Contact Us
              </Link>
              <Link
                to="/booking#reserve-spot"
                className="bg-brand-gold text-brand-black font-semibold px-6 py-3 rounded-md hover:bg-brand-gold-dark transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Book a Class
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Schema.org FAQ structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          }),
        }}
      />
    </main>
  );
}
